/**
 * 保险箱 store：内存密钥（VaultKeys）+ 解锁状态。
 * 安全约定：
 * - 密钥仅驻内存，锁屏/登出/会话失效立即 destroyKeys 清零，绝不持久化
 * - localStorage('lc-vault-kdf') 只存非机密 kdfParams（salt + 包装 DEK 密文）
 */
import { create } from 'zustand';
import {
  changeVaultPassword as libChangePassword,
  clearCachedKdf,
  destroyKeys,
  hasCachedKdf,
  initVault,
  recoverVault,
  unlockVault,
  type ChangeVaultPasswordResult,
  type InitVaultResult,
  type RecoveryMode,
  type VaultKeys,
} from '../lib/vault';

interface VaultState {
  /** 解锁后驻内存的密钥；锁定即 null */
  keys: VaultKeys | null;
  unlocked: boolean;
  /** 本机是否有 kdfParams 缓存（决定 UnlockPanel / 新设备提示） */
  hasLocalKdf: boolean;
  /** 初始化保险箱（首次），返回一次性恢复码 */
  init: (password: string, opts?: { withRecovery?: boolean }) => Promise<InitVaultResult>;
  /** 主密码解锁 */
  unlock: (password: string) => Promise<void>;
  /** 恢复码解锁 */
  recover: (recoveryCode: string) => Promise<void>;
  /** 修改主密码（DEK 不变），mode：恢复码 keep/clear/reset */
  changePassword: (
    currentPassword: string,
    newPassword: string,
    recovery?: RecoveryMode,
  ) => Promise<ChangeVaultPasswordResult>;
  /** 锁定：清零内存密钥 */
  lock: () => void;
  /** 清除本机 kdfParams 缓存（换设备引导 / 重置） */
  clearLocalKdf: () => void;
  syncLocalKdf: () => void;
}

export const useVault = create<VaultState>((set, get) => ({
  keys: null,
  unlocked: false,
  hasLocalKdf: hasCachedKdf(),

  async init(password, opts) {
    const result = await initVault(password, opts);
    get().lock();
    set({ keys: result.keys, unlocked: true, hasLocalKdf: true });
    return result;
  },

  async unlock(password) {
    const keys = await unlockVault(password);
    get().lock();
    set({ keys, unlocked: true, hasLocalKdf: true });
  },

  async recover(recoveryCode) {
    const keys = await recoverVault(recoveryCode);
    get().lock();
    set({ keys, unlocked: true, hasLocalKdf: true });
  },

  async changePassword(currentPassword, newPassword, recovery = 'keep') {
    const result = await libChangePassword(currentPassword, newPassword, recovery);
    get().lock();
    set({ keys: result.keys, unlocked: true, hasLocalKdf: true });
    return result;
  },

  lock() {
    destroyKeys(get().keys);
    set({ keys: null, unlocked: false });
  },

  clearLocalKdf() {
    clearCachedKdf();
    get().lock();
    set({ hasLocalKdf: false });
  },

  syncLocalKdf() {
    set({ hasLocalKdf: hasCachedKdf() });
  },
}));
